import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, MapPin, Award, Briefcase } from "lucide-react";

export default function Directory() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.members({ limit: 200 });
        if (alive) setMembers(Array.isArray(data) ? data : []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const departments = useMemo(() => {
    const set = new Set();
    members.forEach((m) => (m.departments || []).forEach((d) => set.add(d)));
    return Array.from(set).sort();
  }, [members]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return members.filter((m) => {
      const matchesTerm =
        !term ||
        m.name?.toLowerCase().includes(term) ||
        m.headline?.toLowerCase().includes(term) ||
        m.location?.toLowerCase().includes(term) ||
        (m.skills || []).some((s) => s?.toLowerCase().includes(term));
      const matchesDept = !dept || (m.departments || []).includes(dept);
      return matchesTerm && matchesDept;
    });
  }, [members, q, dept]);

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Member Directory</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium">Every department, one crew.</h1>
        <p className="text-neutral-400 mt-3 max-w-xl">Verified crew, producers, and creatives working across Southern Utah.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 bg-black border border-white/10 rounded-full px-4 py-2.5 focus-within:border-[hsl(var(--ff-accent))]">
          <Search className="w-4 h-4 text-neutral-500" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, skill, location…"
            className="bg-transparent flex-1 outline-none text-sm placeholder:text-neutral-600"
            data-testid="directory-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setDept("")}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${dept === "" ? "ff-accent-bg text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
            data-testid="directory-dept-all"
          >
            All
          </button>
          {departments.slice(0, 8).map((d) => (
            <button
              key={d}
              onClick={() => setDept(d)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${dept === d ? "ff-accent-bg text-black" : "bg-white/5 text-neutral-300 hover:bg-white/10"}`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading members…</div>
      ) : filtered.length === 0 ? (
        <div className="ff-card p-10 text-center text-neutral-400" data-testid="directory-empty">
          No members match your search.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ff-stagger" data-testid="directory-grid">
          {filtered.map((m) => (
            <Link key={m.id} to={`/app/members/${m.id}`} className="ff-card p-6 block group" data-testid={`directory-member-${m.id}`}>
              <div className="w-14 h-14 rounded-full ff-accent-bg text-black font-semibold flex items-center justify-center mb-4">
                {m.avatar_url
                  ? <img src={m.avatar_url} className="w-full h-full rounded-full object-cover" alt="" />
                  : (m.name || "?").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}
              </div>
              <div className="font-display text-lg tracking-tight">{m.name}</div>
              <div className="text-xs text-neutral-400 line-clamp-2 mt-1">{m.headline || "Southern Utah film"}</div>
              <div className="flex flex-wrap gap-2 mt-4 text-[10px] uppercase tracking-widest text-neutral-500">
                {m.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.location}</span>}
                {m.verified && <span className="inline-flex items-center gap-1 ff-accent-text"><Award className="w-3 h-3" /> Verified</span>}
                {m.available && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> Available</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
